import axiosInstance from "../../axios/axiosInstance";
import {
  VehicleLog,
  VehicleLogFilters,
  VehicleLogHistoryResponse,
  VehicleLogApiResponse,
  SortField,
  SortOrder,
} from "./types";

export interface ApiResponse<T> {
  data: T;
  status: "ok" | "error";
  error?: { message: string; code: string };
}

export class VehicleLogApi {
  private baseUrl = "/api/vehicles";

  async getVehicleLogs(
    filters: VehicleLogFilters,
    page: number = 1,
    limit: number = 10
  ): Promise<VehicleLogApiResponse> {
    const params = new URLSearchParams();

    // Pagination
    params.append("page", String(page));
    params.append("limit", String(Math.min(limit, 100))); // Max 100

    // Search
    if (filters.search) {
      params.append("search", filters.search);
    }

    // Status filter
    if (filters.status && filters.status !== "all") {
      params.append("status", filters.status);
    }

    // Date filters
    if (filters.startDate) {
      params.append("startDate", filters.startDate);
    } else if (filters.date_from) {
      params.append("startDate", filters.date_from);
    }
    if (filters.endDate) {
      params.append("endDate", filters.endDate);
    } else if (filters.date_to) {
      params.append("endDate", filters.date_to);
    }

    // Node filters
    if (filters.entry_node_id) {
      params.append("entry_node_id", filters.entry_node_id);
    }
    if (filters.exit_node_id) {
      params.append("exit_node_id", filters.exit_node_id);
    }

    // Campus filter
    if (filters.entry_campus_id) {
      params.append("entry_campus_id", String(filters.entry_campus_id));
    } else if (filters.campus_id) {
      params.append("entry_campus_id", String(filters.campus_id));
    }

    // Has exit filter
    if (filters.has_exit !== undefined) {
      params.append("has_exit", filters.has_exit ? "true" : "false");
    }

      const response = await axiosInstance.get<VehicleLogApiResponse>(
        `${this.baseUrl}?${params.toString()}`
      );

      // Sort client-side if needed (API doesn't support sorting)
      if (response.data.status === "ok" && response.data.data) {
        // Sorting will be handled in the component
      }

    if (response.data.status === "ok") {
      return response.data;
    }

    throw new Error("Failed to fetch vehicle logs");
  }

  async getVehicleLogHistory(
    filters: VehicleLogFilters,
    page: number = 1,
    limit: number = 20
  ): Promise<VehicleLogHistoryResponse> {
    const params = new URLSearchParams();

    if (filters.vehicle_type) params.append("vehicle_type", filters.vehicle_type);
    if (filters.make) params.append("make", filters.make);
    if (filters.date_from) params.append("date_from", filters.date_from);
    if (filters.date_to) params.append("date_to", filters.date_to);
    if (filters.is_inside !== undefined) params.append("is_inside", String(filters.is_inside));
    if (filters.owner_name) params.append("owner_name", filters.owner_name);
    if (filters.member_name) params.append("member_name", filters.member_name);
    if (filters.search) params.append("search", filters.search);
    if (filters.campus_id) params.append("campus_id", String(filters.campus_id));
    if (filters.status && filters.status !== "all") params.append("status", filters.status);

    params.append("page", String(page));
    params.append("limit", String(limit));

    const response = await axiosInstance.get<ApiResponse<VehicleLogHistoryResponse>>(
      `/api/vehicle-logs/history?${params.toString()}`
    );
    return response.data?.data || { data: [], total: 0, page, limit, totalPages: 0 };
  }
}

export const vehicleLogApi = new VehicleLogApi();
